'use client'

import { useEffect, useRef, useState } from 'react'

interface FaqItem {
  question: string
  answer: string
}

/**
 * /services/ai-powered-growth -- the body of the page, ported from
 * `public/radar-branding/ai-powered-growth.html`. See
 * `ai-powered-growth.css` for what the port changed and why.
 *
 * Client only for the three behaviours the mock's own script carried:
 *
 *   1. Reveal on scroll. Content renders VISIBLE by default and is armed only
 *      once this mounts (`is-enhanced`), so SSR, a failed hydration and
 *      `prefers-reduced-motion` all show the finished page. The mock hid
 *      `.reveal` unconditionally, which renders a blank document if its script
 *      never runs.
 *   2. The three package `<details>`: all open on desktop, only the middle one
 *      open under 760px. State here rather than imperative `detail.open`
 *      writes, so React owns it.
 *   3. The FAQ show-more toggle.
 *
 * The mock's fourth behaviour, the scroll-progress bar, is dropped: the root
 * layout already renders `ScrollProgress`.
 */
/**
 * `heroPrice` comes from `getServicePriceModel()` in the route -- the same call
 * that builds the JSON-LD Offer. This string used to be hardcoded here, which
 * is exactly the drift that helper exists to prevent: the visible price and the
 * structured-data price have to move together.
 */
export default function AiPoweredGrowthContent({
  faqItems,
  heroPrice,
}: {
  faqItems: readonly FaqItem[]
  heroPrice?: string | null
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [faqOpen, setFaqOpen] = useState(false)
  const [packageOpen, setPackageOpen] = useState([true, true, true])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)')
    const sync = () =>
      setPackageOpen(mq.matches ? [false, true, false] : [true, true, true])
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = Array.from(root.querySelectorAll('.apg-reveal'))
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => el.classList.add('apg-in'))
      return
    }
    setEnhanced(true)
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('apg-in')
            io.unobserve(e.target)
          }
        }),
      { threshold: 0.08, rootMargin: '0px 0px -35px' }
    )
    targets.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={rootRef}
      className={`apg-page${enhanced ? ' apg-is-enhanced' : ''}`}
    >
      <details className='apg-nav-menu'>
        <summary>Sections</summary>
        <div>
          <a href='#engine'>Engine</a>
          <a href='#pricing'>Pricing</a>
          <a href='#loop'>Experiment loop</a>
          <a href='#faq'>FAQ</a>
        </div>
      </details>
      <section className='apg-hero' aria-labelledby='hero-title'>
        <div className='apg-wrap'>
          <div className='apg-hero-grid'>
            <div className='apg-hero-copy apg-reveal apg-in'>
              <p className='apg-label apg-hero-eyebrow'>
                Best for B2B SaaS with a live product under $50K/month spend
              </p>
              <h1 id='hero-title' className='apg-display'>
                Marketing ops for SaaS companies that need{' '}
                <span className='apg-green'>pipeline,</span> not brand
                awareness.
              </h1>
              <p>
                We set up your content, lead scoring, email automation, and
                analytics so you have a repeatable way to generate qualified
                leads every month. Not for pre-PMF startups (growth cannot fix a
                product nobody wants) or teams expecting results in month 1
                (setup takes month 1, results start month 2-3).
              </p>
              <p className='apg-hero-support'>
                Content marketing, lead generation, and growth ops delivered as
                a retainer.
              </p>
              <div className='apg-hero-actions'>
                <a className='apg-btn apg-btn-amber' href='/contact-us'>
                  Build my growth engine{' '}
                  <span className='apg-arrow' aria-hidden>
                    ↗
                  </span>
                </a>
              </div>
              {heroPrice ? <p className='apg-hero-price'>{heroPrice}</p> : null}
            </div>
            <div
              className='apg-funnel-console apg-reveal apg-in'
              aria-label='Growth funnel monitor'
            >
              <div className='apg-console-head'>
                <p className='apg-label apg-label-blue'>Pipeline flow / live</p>
                <span className='apg-live-status'>
                  <i aria-hidden></i> Revenue path active
                </span>
              </div>
              <div className='apg-funnel-frame'>
                <svg
                  viewBox='0 0 240 240'
                  role='img'
                  aria-label='Animated funnel showing leads flowing from acquisition through nurture to converted pipeline'
                >
                  <line
                    x1='120'
                    y1='46'
                    x2='120'
                    y2='200'
                    style={{ stroke: 'var(--g-diagram-ring)' }}
                    strokeWidth='1'
                    opacity='0.4'
                  />
                  <path
                    d='M44,50 L196,50 L170,94 L70,94 Z'
                    style={{
                      fill: 'color-mix(in srgb,var(--g-accent-blue-text) 12%,transparent)',
                      stroke: 'var(--g-accent-blue-text)',
                    }}
                    strokeWidth='1'
                  />
                  <path
                    d='M74,102 L166,102 L146,146 L94,146 Z'
                    style={{
                      fill: 'color-mix(in srgb,var(--g-accent-amber-text) 12%,transparent)',
                      stroke: 'var(--g-accent-amber-text)',
                    }}
                    strokeWidth='1'
                  />
                  <path
                    d='M98,154 L142,154 L128,196 L114,196 Z'
                    style={{
                      fill: 'color-mix(in srgb,var(--g-accent-green-text) 14%,transparent)',
                      stroke: 'var(--g-accent-green-text)',
                    }}
                    strokeWidth='1'
                  />
                  <circle
                    cx='120'
                    cy='46'
                    r='3.5'
                    className='apg-pm-growth-flow'
                    style={{
                      fill: 'var(--g-accent-blue-text)',
                      animationDelay: '0s',
                    }}
                  />
                  <circle
                    cx='120'
                    cy='46'
                    r='3.5'
                    className='apg-pm-growth-flow'
                    style={{
                      fill: 'var(--g-accent-blue-text)',
                      animationDelay: '0.7s',
                    }}
                  />
                  <circle
                    cx='120'
                    cy='46'
                    r='3.5'
                    className='apg-pm-growth-flow'
                    style={{
                      fill: 'var(--g-accent-blue-text)',
                      animationDelay: '1.4s',
                    }}
                  />
                  <circle
                    cx='120'
                    cy='46'
                    r='3.5'
                    className='apg-pm-growth-flow'
                    style={{
                      fill: 'var(--g-accent-blue-text)',
                      animationDelay: '2.1s',
                    }}
                  />
                </svg>
              </div>
              <p className='apg-funnel-caption'>
                Leads flow from acquisition to pipeline, every month.
              </p>
              <div className='apg-funnel-legend' aria-label='Funnel stages'>
                <span>
                  <i aria-hidden></i>Acquire
                </span>
                <span>
                  <i aria-hidden></i>Nurture
                </span>
                <span>
                  <i aria-hidden></i>Convert
                </span>
              </div>
            </div>
          </div>
          <ol
            className='apg-pipeline-ledger apg-reveal'
            aria-label='Pipeline operating path'
          >
            <li className='apg-ledger-step'>
              <b>Input / ICP signal</b>
            </li>
            <li className='apg-ledger-step'>
              <b>Acquire</b>
            </li>
            <li className='apg-ledger-step'>
              <b>Nurture</b>
            </li>
            <li className='apg-ledger-step'>
              <b>Convert</b>
            </li>
            <li className='apg-ledger-step'>
              <b>Qualified pipeline</b>
            </li>
          </ol>
        </div>
      </section>

      <section className='apg-context' aria-labelledby='context-title'>
        <div className='apg-wrap'>
          <div className='apg-context-grid apg-reveal'>
            <h2 id='context-title' className='apg-display'>
              A repeatable way to generate qualified leads.
            </h2>
            <p className='apg-context-copy'>
              You have a product people want but no repeatable way to get it in
              front of them. We set up your content engine, lead scoring, email
              sequences, and analytics dashboard so qualified leads come in
              every month without scaling headcount. Month 1 is setup and
              baseline. Results start month 2-3. If we cannot move your numbers,
              you will know within 90 days.
            </p>
          </div>
          <div className='apg-stats apg-reveal'>
            <article className='apg-stat'>
              <strong>30 days</strong>
              <p className='apg-label'>To full-funnel baseline</p>
              <p>
                Acquisition, nurture, and sales-enablement dashboards wired to
                revenue KPIs in month one.
              </p>
            </article>
            <article className='apg-stat'>
              <strong>One dashboard</strong>
              <p className='apg-label'>Marketing, sales, product</p>
              <p>
                Lead scoring and routing automations surface the hottest
                opportunities instantly.
              </p>
            </article>
            <article className='apg-stat'>
              <strong>No new hires</strong>
              <p className='apg-label'>Content stays fed</p>
              <p>
                AI-assisted production keeps every channel fresh without scaling
                the team.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='apg-contrast' aria-labelledby='contrast-title'>
        <div className='apg-wrap'>
          <div className='apg-section-head apg-reveal'>
            <div>
              <p className='apg-label apg-label-pink'>
                Brand awareness vs pipeline
              </p>
              <h2 id='contrast-title' className='apg-display'>
                Awareness feels good. Pipeline pays.
              </h2>
            </div>
            <p>Awareness feels good. Pipeline pays. We build the second one.</p>
          </div>
          <div className='apg-contrast-board apg-reveal'>
            <article className='apg-contrast-side'>
              <h3>Brand awareness</h3>
              <div className='apg-contrast-list'>
                <div className='apg-contrast-row'>
                  <i>01</i>Impressions and reach
                </div>
                <div className='apg-contrast-row'>
                  <i>02</i>Likes and follows
                </div>
                <div className='apg-contrast-row'>
                  <i>03</i>No path to revenue
                </div>
              </div>
            </article>
            <article className='apg-contrast-side'>
              <h3>Pipeline</h3>
              <div className='apg-contrast-list'>
                <div className='apg-contrast-row'>
                  <i>01</i>Qualified, scored leads
                </div>
                <div className='apg-contrast-row'>
                  <i>02</i>Routed to sales in minutes
                </div>
                <div className='apg-contrast-row'>
                  <i>03</i>Tied to revenue
                </div>
              </div>
            </article>
            <div className='apg-contrast-gap'>
              The gap: vanity metrics do not close deals. We wire content,
              scoring, and automation to one number that does: qualified
              pipeline.
            </div>
          </div>
        </div>
      </section>

      <section
        className='apg-pricing'
        id='pricing'
        aria-labelledby='pricing-title'
      >
        <div className='apg-wrap'>
          <div className='apg-section-head apg-reveal'>
            <div>
              <p className='apg-label'>Retainer scopes / 03</p>
              <h2 id='pricing-title' className='apg-display'>
                Pricing &<br />
                Packages
              </h2>
            </div>
            <p>
              Choose the package that fits your needs. All packages include
              post-launch support.
            </p>
          </div>
          <div className='apg-package-grid apg-reveal'>
            <article className='apg-package' id='starter'>
              <header className='apg-package-head'>
                <span className='apg-package-code'>SCOPE / STARTER</span>
                <h3>Starter</h3>
                <p className='apg-package-price'>$2,995-$4,995</p>
                <p className='apg-package-audience'>
                  Startups testing channels
                </p>
              </header>
              <details className='apg-package-detail' open={packageOpen[0]}>
                <summary>
                  View package details <span aria-hidden>+</span>
                </summary>
                <ul className='apg-package-list'>
                  <li>Growth strategy and channel selection</li>
                  <li>2 primary channels (SEO + Email or Content + Social)</li>
                  <li>Content creation (4-8 pieces/month)</li>
                  <li>Email marketing (2 campaigns/month)</li>
                  <li>Analytics setup and monthly reporting</li>
                  <li>A/B testing (2-3 tests/month)</li>
                  <li>Monthly strategy calls (2 hours)</li>
                  <li>Basic automation setup</li>
                </ul>
                <footer className='apg-package-time'>
                  <span>Estimated Timeline:</span>
                  <strong>Month-to-month, 3-month minimum</strong>
                </footer>
              </details>
            </article>
            <article className='apg-package' id='growth'>
              <header className='apg-package-head'>
                <span className='apg-popular'>Most Popular</span>
                <span className='apg-package-code'>SCOPE / GROWTH</span>
                <h3>Growth</h3>
                <p className='apg-package-price'>$5,995-$9,995</p>
                <p className='apg-package-highlight'>
                  Only ₱165K more but includes 4-5 channels + more content +
                  dedicated manager
                </p>
                <p className='apg-package-audience'>
                  Scaling companies with proven product-market fit
                </p>
              </header>
              <details className='apg-package-detail' open={packageOpen[1]}>
                <summary>
                  View package details <span aria-hidden>+</span>
                </summary>
                <ul className='apg-package-list'>
                  <li>Everything in Starter, plus:</li>
                  <li>Comprehensive growth strategy</li>
                  <li>
                    4-5 channels (SEO, Content, Email, Social, Partnerships)
                  </li>
                  <li>Content production (12-20 pieces/month)</li>
                  <li>Email marketing (4-6 campaigns + nurture)</li>
                  <li>Social media management (15-25 posts/month)</li>
                  <li>Paid advertising strategy and execution</li>
                  <li>CRO (5-10 tests/month)</li>
                  <li>Marketing automation</li>
                  <li>Advanced analytics</li>
                  <li>Weekly strategy calls</li>
                  <li>Dedicated growth manager</li>
                </ul>
                <footer className='apg-package-time'>
                  <span>Estimated Timeline:</span>
                  <strong>Month-to-month, 6-month minimum</strong>
                </footer>
              </details>
            </article>
            <article className='apg-package' id='scale'>
              <header className='apg-package-head'>
                <span className='apg-package-code'>SCOPE / SCALE</span>
                <h3>Scale</h3>
                <p className='apg-package-price'>$12,995-$19,995</p>
                <p className='apg-package-audience'>
                  Aggressive scaling and full-funnel optimization
                </p>
              </header>
              <details className='apg-package-detail' open={packageOpen[2]}>
                <summary>
                  View package details <span aria-hidden>+</span>
                </summary>
                <ul className='apg-package-list'>
                  <li>Everything in Accelerator, plus:</li>
                  <li>Full-funnel growth strategy</li>
                  <li>All growth channels</li>
                  <li>High-volume content (30-50 pieces/month)</li>
                  <li>Email marketing (10-15 campaigns + automation)</li>
                  <li>Social media (30-50 posts/month)</li>
                  <li>Paid advertising across all platforms</li>
                  <li>Advanced CRO (15-25 tests/month)</li>
                  <li>Predictive analytics and AI personalization</li>
                  <li>Marketing tech stack optimization</li>
                  <li>Account-based marketing (ABM)</li>
                  <li>Attribution modeling</li>
                  <li>Dedicated growth team</li>
                  <li>Executive reporting</li>
                </ul>
                <footer className='apg-package-time'>
                  <span>Estimated Timeline:</span>
                  <strong>Month-to-month, 12-month minimum</strong>
                </footer>
              </details>
            </article>
          </div>
          <div className='apg-compare apg-reveal'>
            <div className='apg-compare-intro'>
              <p className='apg-label'>Agency comparison</p>
              <h3>Why Pixelmojo?</h3>
              <p>Compare traditional agencies vs our AI-powered approach</p>
            </div>
            <div className='apg-compare-grid'>
              <div className='apg-compare-col'>
                <div className='apg-compare-title'>Traditional Agency</div>
                <div className='apg-compare-row'>6-12 month timelines</div>
                <div className='apg-compare-row'>$50K-$200K+ budgets</div>
                <div className='apg-compare-row'>Fixed deliverables</div>
                <div className='apg-compare-row'>Limited revisions</div>
                <div className='apg-compare-row'>Handoff and done</div>
                <div className='apg-compare-row'>No ongoing optimization</div>
              </div>
              <div className='apg-compare-col'>
                <div className='apg-compare-title'>Pixelmojo (AI-Powered)</div>
                <div className='apg-compare-row'>4-12 week delivery</div>
                <div className='apg-compare-row'>$1,995-$49,995 budgets</div>
                <div className='apg-compare-row'>Flexible and iterative</div>
                <div className='apg-compare-row'>Collaborative process</div>
                <div className='apg-compare-row'>
                  Post-launch support included
                </div>
                <div className='apg-compare-row'>Built for optimization</div>
              </div>
            </div>
          </div>
          <div className='apg-call-band apg-reveal'>
            <div className='apg-call-main'>
              <div>
                <p className='apg-label apg-label-blue'>
                  Free 30-Min Strategy Call
                </p>
                <h3>Let's talk about your project</h3>
                <p>
                  We'll review your needs, discuss options, and create a custom
                  proposal. No pressure, no obligation.
                </p>
              </div>
              <a className='apg-btn apg-btn-amber' href='/contact-us'>
                Book your free call{' '}
                <span className='apg-arrow' aria-hidden>
                  →
                </span>
              </a>
            </div>
            <aside className='apg-call-local'>
              <h4>🇵🇭 Philippines Startup Special</h4>
              <p className='apg-discount'>15-20% off</p>
              <p>
                We're based in the Philippines and want to support local
                startups. Book a call to see if you qualify.
              </p>
            </aside>
          </div>
        </div>
      </section>

      <section className='apg-routes' aria-labelledby='routes-title'>
        <div className='apg-wrap'>
          <div className='apg-section-head apg-reveal'>
            <div>
              <p className='apg-label apg-label-pink'>Three ways to run it</p>
              <h2 id='routes-title' className='apg-display'>
                Test channels or run the full funnel.
              </h2>
            </div>
            <p>
              Test a couple of channels or run the full funnel. You own every
              workflow and dashboard.
            </p>
          </div>
          <div className='apg-route-list apg-reveal'>
            <a className='apg-route' href='#starter'>
              <span className='apg-route-code'>01</span>
              <h3>Growth Starter</h3>
              <p>Startups testing channels</p>
              <strong>$2,995 to $4,995/mo</strong>
              <span className='apg-route-scope'>2 channels</span>
              <span className='apg-route-arrow apg-arrow' aria-hidden>
                ↘
              </span>
            </a>
            <a className='apg-route' href='#growth'>
              <span className='apg-route-code'>02 / Most popular</span>
              <h3>Growth Accelerator</h3>
              <p>Scaling companies with product-market fit</p>
              <strong>$5,995 to $9,995/mo</strong>
              <span className='apg-route-scope'>4 to 5 channels</span>
              <span className='apg-route-arrow apg-arrow' aria-hidden>
                →
              </span>
            </a>
            <a className='apg-route' href='#scale'>
              <span className='apg-route-code'>03</span>
              <h3>Growth Enterprise</h3>
              <p>Aggressive, full-funnel scaling</p>
              <strong>$12,995 to $19,995/mo</strong>
              <span className='apg-route-scope'>All channels</span>
              <span className='apg-route-arrow apg-arrow' aria-hidden>
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>

      <section
        className='apg-dashboard'
        id='engine'
        aria-labelledby='dashboard-title'
      >
        <div className='apg-wrap apg-dashboard-grid'>
          <div className='apg-dashboard-copy apg-reveal'>
            <p className='apg-label'>How it works</p>
            <h2 id='dashboard-title' className='apg-display'>
              One dashboard. Every team sees the same numbers.
            </h2>
            <p>
              We connect your marketing, sales, and product data into shared
              dashboards so everyone stops arguing about attribution and starts
              acting on the same truth.
            </p>
            <ul className='apg-dashboard-points'>
              <li>
                Unified contact graph merges product usage, campaign history,
                and account intent.
              </li>
              <li>
                AI scoring models prioritize accounts and leads that match your
                highest-value customers.
              </li>
              <li>
                Alerting, routing, and playbooks keep teams executing within
                minutes, not days.
              </li>
            </ul>
          </div>
          <div className='apg-data-board apg-reveal'>
            <div className='apg-data-head'>
              <p className='apg-label'>Revenue ledger</p>
              <span className='apg-label apg-label-green'>
                All teams / synced
              </span>
            </div>
            <div className='apg-data-title'>
              <h3>One dashboard, every team</h3>
            </div>
            <div className='apg-data-row'>
              <span>01</span>
              <strong>Marketing campaigns and attribution</strong>
              <i className='apg-signal' aria-hidden></i>
            </div>
            <div className='apg-data-row'>
              <span>02</span>
              <strong>Sales pipeline and routing</strong>
              <i className='apg-signal' aria-hidden></i>
            </div>
            <div className='apg-data-row'>
              <span>03</span>
              <strong>Product usage signals</strong>
              <i className='apg-signal' aria-hidden></i>
            </div>
            <div className='apg-data-row'>
              <span>04</span>
              <strong>AI lead scoring on top</strong>
              <i className='apg-signal' aria-hidden></i>
            </div>
            <div className='apg-data-foot'>
              Everyone stops arguing about attribution and acts on the same
              truth.
            </div>
          </div>
        </div>
      </section>

      <section className='apg-engines' aria-labelledby='engines-title'>
        <div className='apg-wrap'>
          <div className='apg-engines-head apg-reveal'>
            <p className='apg-label apg-label-blue'>Growth engines</p>
            <h2 id='engines-title' className='apg-display'>
              Growth engines we deploy together
            </h2>
            <p>
              Every play is documented, automated, and measured. Your team knows
              exactly how each channel contributes to revenue.
            </p>
          </div>
          <div className='apg-engine-grid apg-reveal'>
            <article className='apg-engine'>
              <p className='apg-label apg-label-blue'>Acquisition</p>
              <h3>Demand engines that never sleep</h3>
              <ul>
                <li>
                  SEO, paid, and outbound plays orchestrated from a single
                  experimentation backlog.
                </li>
                <li>
                  Channel-specific content tuned by AI to stay on message and
                  deliver consistent velocity.
                </li>
                <li>
                  Account intent monitoring so reps get context before prospects
                  ever hit reply.
                </li>
              </ul>
            </article>
            <article className='apg-engine'>
              <p className='apg-label' style={{ color: 'var(--g-amber-ink)' }}>
                Lifecycle
              </p>
              <h3>Nurture journeys that progress deals</h3>
              <ul>
                <li>
                  Segmented nurture streams blending email, in-app, and
                  community touchpoints.
                </li>
                <li>
                  Milestone-based playbooks triggered by product usage and sales
                  stages.
                </li>
                <li>
                  Self-serve education hubs and AI chat assistance to reduce
                  support drag.
                </li>
              </ul>
            </article>
            <article className='apg-engine'>
              <p className='apg-label apg-label-green'>Revenue Ops</p>
              <h3>Data and process that stay aligned</h3>
              <ul>
                <li>
                  Unified dashboards combining CRM, product analytics, and
                  marketing attribution.
                </li>
                <li>
                  Automation governance to keep enrichment, scoring, and routing
                  accurate.
                </li>
                <li>
                  RevOps health checks that prevent broken handoffs between
                  teams.
                </li>
              </ul>
            </article>
            <article className='apg-engine'>
              <p className='apg-label apg-label-pink'>Advocacy</p>
              <h3>Communities that compound reach</h3>
              <ul>
                <li>
                  Advocate identification and enablement layered into lifecycle
                  journeys.
                </li>
                <li>
                  Customer storytelling sprints that turn wins into content
                  within days.
                </li>
                <li>
                  Partner and reseller motions orchestrated alongside direct
                  acquisition.
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className='apg-loop' id='loop' aria-labelledby='loop-title'>
        <div className='apg-wrap'>
          <div className='apg-section-head apg-reveal'>
            <div>
              <p className='apg-label'>The growth experiment loop</p>
              <h2 id='loop-title' className='apg-display'>
                A repeating cycle with clear ownership.
              </h2>
            </div>
            <p>
              A repeating cycle with clear ownership, so wins scale and
              learnings stick.
            </p>
          </div>
          <div className='apg-loop-track apg-reveal'>
            <article className='apg-loop-stage'>
              <span className='apg-loop-dot' aria-hidden></span>
              <p className='apg-label'>Weekly</p>
              <h3>Signal sync</h3>
              <p>Unify data and score hypotheses by impact and confidence.</p>
            </article>
            <article className='apg-loop-stage'>
              <span className='apg-loop-dot' aria-hidden></span>
              <p className='apg-label'>Bi-weekly</p>
              <h3>Sprint deployment</h3>
              <p>Ship creative, copy, and pages as bundles across channels.</p>
            </article>
            <article className='apg-loop-stage'>
              <span className='apg-loop-dot' aria-hidden></span>
              <p className='apg-label'>Monthly</p>
              <h3>Evidence review</h3>
              <p>Double down on what converts, retire what slows pipeline.</p>
            </article>
          </div>
          <div className='apg-loop-repeat apg-reveal'>
            <span>Repeats every cycle, compounding wins</span>
            <span className='apg-arrow' aria-hidden>
              ↺
            </span>
          </div>
        </div>
      </section>

      <section className='apg-content-system' aria-labelledby='content-title'>
        <div className='apg-wrap apg-content-grid'>
          <div className='apg-content-copy apg-reveal'>
            <p className='apg-label'>Content production</p>
            <h2 id='content-title' className='apg-display'>
              We keep your content channels fed without scaling headcount.
            </h2>
            <p>
              Writers, designers, and AI copilots work from the same brief. More
              campaigns ship, more often, without hiring more people.
            </p>
            <ul className='apg-content-points'>
              <li>
                Prompt libraries produce channel-ready copy, motion graphics,
                and social cuts.
              </li>
              <li>
                Editorial calendar synced to revenue goals with pre-approved
                story angles.
              </li>
              <li>
                Feedback loops push performance insights back into creative
                briefs instantly.
              </li>
            </ul>
          </div>
          <div className='apg-content-queue apg-reveal'>
            <div className='apg-queue-head'>
              <p className='apg-label'>Content queue</p>
              <span className='apg-label apg-label-green'>Fed / active</span>
            </div>
            <div className='apg-queue-title'>
              <h3>Content that stays fed</h3>
            </div>
            <div className='apg-queue-row'>
              <span>01</span>
              <strong>Prompt libraries for copy, motion, social</strong>
              <span className='apg-queue-status'>Ready</span>
            </div>
            <div className='apg-queue-row'>
              <span>02</span>
              <strong>Editorial calendar synced to revenue</strong>
              <span className='apg-queue-status'>Synced</span>
            </div>
            <div className='apg-queue-row'>
              <span>03</span>
              <strong>Feedback loops back into briefs</strong>
              <span className='apg-queue-status'>Learning</span>
            </div>
            <div className='apg-queue-foot'>
              More campaigns ship, more often, without hiring more people.
            </div>
          </div>
        </div>
      </section>

      <section className='apg-visibility' aria-labelledby='visibility-title'>
        <div className='apg-wrap'>
          <p className='apg-label apg-label-pink'>Leadership visibility</p>
          <h2 id='visibility-title' className='apg-display apg-reveal'>
            Visibility that keeps leadership confident
          </h2>
          <div className='apg-visibility-grid apg-reveal'>
            <article className='apg-visibility-item'>
              <span className='apg-label apg-label-blue'>Shared truth</span>
              <h3>Revenue command center</h3>
              <p>
                Shared dashboards for marketing, sales, and success with live
                projections.
              </p>
            </article>
            <article className='apg-visibility-item'>
              <span className='apg-label apg-label-pink'>Evidence memory</span>
              <h3>Experiment library</h3>
              <p>
                Archive of tests, learnings, and assets so teams avoid repeating
                work.
              </p>
            </article>
            <article className='apg-visibility-item'>
              <span className='apg-label apg-label-green'>Operations</span>
              <h3>Growth ops runbook</h3>
              <p>
                Process documentation covering triggers, ownership, and
                escalation paths.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='apg-faq' id='faq' aria-labelledby='faq-title'>
        <div className='apg-wrap apg-faq-grid'>
          <div className='apg-faq-title apg-reveal'>
            <p className='apg-label'>Questions / answered</p>
            <h2 id='faq-title' className='apg-display'>
              Frequently Asked Questions
            </h2>
            <p>Everything you need to know about working with Pixelmojo.</p>
          </div>
          <div className='apg-faq-list apg-reveal'>
            {faqItems.slice(0, 4).map((item, index) => (
              <details key={item.question} open={index === 0}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
            {faqItems.slice(4).map((item, index) => (
              <details
                className='apg-faq-more'
                id={index === 0 ? 'faq-more' : undefined}
                key={item.question}
                hidden={!faqOpen}
              >
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
            {faqItems.length > 4 ? (
              <button
                className='apg-show-more'
                type='button'
                aria-expanded={faqOpen}
                aria-controls='faq-more'
                onClick={() => setFaqOpen(v => !v)}
              >
                {faqOpen ? 'Show fewer' : `Show ${faqItems.length - 4} more`}
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <section className='apg-proof' aria-labelledby='proof-title'>
        <div className='apg-wrap'>
          <p className='apg-label'>Proof, not promises</p>
          <h2 id='proof-title' className='apg-display apg-reveal'>
            We run the same engine we sell
          </h2>
          <div className='apg-proof-grid apg-reveal'>
            <article className='apg-proof-item'>
              <h3>We run it too</h3>
              <p>
                The content and lead engine we set up for you powers our own
                pipeline and the AI Search Playbook.
              </p>
            </article>
            <article className='apg-proof-item'>
              <h3>Month 1 baseline</h3>
              <p>
                Setup and baseline first, results month 2-3. Honest
                expectations, not inflated promises.
              </p>
            </article>
            <article className='apg-proof-item'>
              <h3>You own it</h3>
              <p>
                Every workflow, automation, and dashboard stays with your team
                if we ever part ways.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className='apg-final-cta' aria-labelledby='cta-title'>
        <div className='apg-wrap apg-cta-grid'>
          <h2 id='cta-title' className='apg-display apg-reveal'>
            Let's see if your pipeline problem is solvable.
          </h2>
          <div className='apg-cta-side apg-reveal'>
            <p>
              30-minute call. We will ask about your current pipeline, your
              traffic, and what you have tried. If growth marketing is not your
              problem, we will tell you what is.
            </p>
            <a className='apg-btn apg-btn-dark' href='/contact-us'>
              Book a growth working session{' '}
              <span className='apg-arrow' aria-hidden>
                ↗
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
